import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  MessageSquare, 
  Heart,
  Send,
  Plus,
  Lock,
  Sparkles,
  ArrowRight,
  Loader2,
  Filter,
  TrendingUp,
  Clock,
  Flame,
  ChevronRight,
  X,
  MoreHorizontal,
  Trash2,
  Award,
  Briefcase,
  FileText,
  UserCheck,
  Target,
  PartyPopper
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  career_advice: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  job_search: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
  resume_review: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  interview_prep: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  networking: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-400',
  wins: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400',
  general: 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400'
};

const CATEGORY_ICONS = {
  career_advice: Target,
  job_search: Briefcase,
  resume_review: FileText,
  interview_prep: MessageSquare,
  networking: UserCheck,
  wins: PartyPopper,
  general: Users
};

const Community = () => {
  const { token, user } = useAuth();
  const { hasFullAccess, isPreviewMode } = useSubscription();
  const [activeView, setActiveView] = useState('feed');
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // New post state
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPostCategory, setNewPostCategory] = useState('');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // View post state
  const [selectedPost, setSelectedPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingPost, setLoadingPost] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (hasFullAccess) {
      fetchPosts();
    } else {
      setLoading(false);
    }
  }, [token, hasFullAccess]);

  useEffect(() => {
    if (hasFullAccess) {
      setPage(1);
      fetchPosts(1, selectedCategory, sortBy);
    }
  }, [selectedCategory, sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchPosts = async (pageNum = 1, category = '', sort = 'latest') => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 10,
        sort
      });
      if (category) params.append('category', category);
      
      const response = await axios.get(`${API_URL}/api/community/posts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (pageNum === 1) {
        setPosts(response.data.posts);
      } else {
        setPosts(prev => [...prev, ...response.data.posts]);
      }
      setTotalPages(response.data.pages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostCategory || !newPostTitle.trim() || !newPostContent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/community/posts`, {
        category: newPostCategory,
        title: newPostTitle,
        content: newPostContent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Post created successfully!');
      setShowNewPost(false);
      setNewPostCategory('');
      setNewPostTitle('');
      setNewPostContent('');
      fetchPosts(1, selectedCategory, sortBy);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikePost = async (postId, e) => {
    e?.stopPropagation();
    try {
      const response = await axios.post(`${API_URL}/api/community/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update post in list
      setPosts(prev => prev.map(p => 
        p.post_id === postId 
          ? { ...p, likes_count: response.data.likes_count, user_has_liked: response.data.user_has_liked }
          : p
      ));
      
      // Update selected post if viewing
      if (selectedPost?.post_id === postId) {
        setSelectedPost(prev => ({
          ...prev,
          likes_count: response.data.likes_count,
          user_has_liked: response.data.user_has_liked
        }));
      }
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      setPosts(prev => prev.filter(p => p.post_id !== postId));
      if (selectedPost?.post_id === postId) {
        setSelectedPost(null);
        setActiveView('feed');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const viewPost = async (post) => {
    setSelectedPost(post);
    setActiveView('post');
    setLoadingPost(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/community/posts/${post.post_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data.post);
      setPostComments(response.data.comments);
    } catch (error) {
      toast.error('Failed to load post');
    } finally {
      setLoadingPost(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/community/posts/${selectedPost.post_id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPostComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      
      // Update comments count in post
      setSelectedPost(prev => ({ ...prev, comments_count: prev.comments_count + 1 }));
      setPosts(prev => prev.map(p => 
        p.post_id === selectedPost.post_id 
          ? { ...p, comments_count: p.comments_count + 1 }
          : p
      ));
      
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/community/comments/${commentId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPostComments(prev => prev.map(c =>
        c.comment_id === commentId
          ? { ...c, likes_count: response.data.likes_count, user_has_liked: response.data.user_has_liked }
          : c
      ));
    } catch (error) {
      toast.error('Failed to like comment');
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  // Preview mode content
  if (isPreviewMode) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8" data-testid="community-page">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">
              Atlas Member Community
            </h1>
            <p className="text-navy-600 dark:text-navy-300">
              Connect with professionals, share wins, ask questions, and grow together
            </p>
          </div>

          {/* Membership Required Banner */}
          <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-navy-800 mb-8">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-navy-900 dark:text-white mb-3">
                Community Access Included with Membership
              </h2>
              <p className="text-navy-600 dark:text-navy-300 max-w-xl mx-auto mb-6">
                Join a community of veterans and professionals working together on career growth. 
                Share experiences, get advice, and celebrate wins together.
              </p>
              <Link to="/membership">
                <Button className="bg-amber-600 hover:bg-amber-700" size="lg">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Activate Membership
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Preview Sections */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Target, name: 'Career Advice', desc: 'Get guidance from experienced professionals' },
              { icon: Briefcase, name: 'Job Search Help', desc: 'Support in your job search journey' },
              { icon: FileText, name: 'Resume Review', desc: 'Get feedback on your resume' },
              { icon: MessageSquare, name: 'Interview Prep', desc: 'Prepare for interviews together' },
              { icon: UserCheck, name: 'Networking', desc: 'Connect with fellow members' },
              { icon: PartyPopper, name: 'Wins & Milestones', desc: 'Celebrate achievements together' }
            ].map((section, idx) => (
              <Card key={idx} className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-navy-100 dark:bg-navy-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-5 h-5 text-navy-600 dark:text-navy-300" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900 dark:text-white">{section.name}</h4>
                      <p className="text-sm text-navy-500 dark:text-navy-400">{section.desc}</p>
                    </div>
                    <Lock className="w-4 h-4 text-navy-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Community Guidelines */}
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-900 dark:text-white">
                <Award className="w-5 h-5 text-amber-600" />
                Community Standards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-navy-600 dark:text-navy-300">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                  Professional and respectful communication
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                  Career-focused discussion
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                  No spam or self-promotion without value
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                  Supportive, growth-oriented environment
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="community-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-1">
              Atlas Member Community
            </h1>
            <p className="text-navy-600 dark:text-navy-300">
              Learn, network, and stay accountable with professionals on the same journey
            </p>
          </div>
          {activeView === 'feed' && (
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setShowNewPost(true)}
              data-testid="create-post-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Post
            </Button>
          )}
          {activeView === 'post' && (
            <Button 
              variant="outline"
              className="dark:border-navy-600 dark:text-white"
              onClick={() => {
                setActiveView('feed');
                setSelectedPost(null);
              }}
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Back to Feed
            </Button>
          )}
        </div>

        {/* New Post Modal */}
        {showNewPost && (
          <Card className="border-amber-200 dark:border-amber-800 mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-navy-900 dark:text-white">Create New Post</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowNewPost(false)}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Select value={newPostCategory} onValueChange={setNewPostCategory}>
                  <SelectTrigger className="dark:bg-navy-700 dark:border-navy-600" data-testid="post-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Post title"
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
                className="dark:bg-navy-700 dark:border-navy-600"
                data-testid="post-title-input"
              />
              <Textarea
                placeholder="Share your thoughts, questions, or experiences..."
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                className="min-h-[120px] dark:bg-navy-700 dark:border-navy-600"
                data-testid="post-content-input"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewPost(false)} className="dark:border-navy-600 dark:text-white">
                  Cancel
                </Button>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={handleCreatePost}
                  disabled={submitting}
                  data-testid="submit-post-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Post to Community
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feed View */}
        {activeView === 'feed' && (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Main Feed */}
            <div className="lg:col-span-3 space-y-4">
              {/* Filters */}
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-3">
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[180px] dark:bg-navy-700 dark:border-navy-600" data-testid="filter-category">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <div className="flex gap-1 bg-navy-100 dark:bg-navy-700 rounded-lg p-1">
                      {[
                        { id: 'latest', icon: Clock, label: 'Latest' },
                        { id: 'active', icon: Flame, label: 'Active' },
                        { id: 'trending', icon: TrendingUp, label: 'Trending' }
                      ].map(sort => (
                        <Button
                          key={sort.id}
                          variant="ghost"
                          size="sm"
                          className={`${sortBy === sort.id ? 'bg-white dark:bg-navy-600 shadow-sm' : ''}`}
                          onClick={() => setSortBy(sort.id)}
                          data-testid={`sort-${sort.id}`}
                        >
                          <sort.icon className="w-4 h-4 mr-1" />
                          {sort.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Posts */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                </div>
              ) : posts.length > 0 ? (
                <>
                  {posts.map(post => {
                    const CategoryIcon = CATEGORY_ICONS[post.category] || Users;
                    return (
                      <Card 
                        key={post.post_id}
                        className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => viewPost(post)}
                        data-testid={`post-${post.post_id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            {/* User Avatar */}
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                              {post.user_picture ? (
                                <img src={post.user_picture} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-white font-semibold text-lg">
                                  {post.user_name?.charAt(0)?.toUpperCase() || 'M'}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              {/* Header */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                  <p className="font-semibold text-navy-900 dark:text-white">{post.user_name}</p>
                                  <div className="flex items-center gap-2 text-sm text-navy-500 dark:text-navy-400">
                                    <span>{formatTimeAgo(post.created_at)}</span>
                                    <span>•</span>
                                    <Badge className={CATEGORY_COLORS[post.category]}>
                                      <CategoryIcon className="w-3 h-3 mr-1" />
                                      {getCategoryName(post.category)}
                                    </Badge>
                                  </div>
                                </div>
                                {post.user_id === user?.user_id && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-navy-400 hover:text-red-500"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePost(post.post_id);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                              
                              {/* Content */}
                              <h3 className="font-heading text-lg font-semibold text-navy-900 dark:text-white mb-2">
                                {post.title}
                              </h3>
                              <p className="text-navy-600 dark:text-navy-300 line-clamp-3 mb-4">
                                {post.content}
                              </p>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`gap-2 ${post.user_has_liked ? 'text-red-500' : 'text-navy-500 dark:text-navy-400'}`}
                                  onClick={(e) => handleLikePost(post.post_id, e)}
                                >
                                  <Heart className={`w-4 h-4 ${post.user_has_liked ? 'fill-current' : ''}`} />
                                  {post.likes_count || 0}
                                </Button>
                                <Button variant="ghost" size="sm" className="gap-2 text-navy-500 dark:text-navy-400">
                                  <MessageSquare className="w-4 h-4" />
                                  {post.comments_count || 0}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* Load More */}
                  {page < totalPages && (
                    <div className="text-center pt-4">
                      <Button
                        variant="outline"
                        className="dark:border-navy-600 dark:text-white"
                        onClick={() => fetchPosts(page + 1, selectedCategory, sortBy)}
                        disabled={loadingMore}
                      >
                        {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Load More
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                  <CardContent className="py-16 text-center">
                    <Users className="w-16 h-16 text-navy-200 dark:text-navy-600 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-navy-700 dark:text-navy-300 mb-2">
                      No posts yet
                    </h3>
                    <p className="text-navy-500 dark:text-navy-400 mb-4">
                      Be the first to start a conversation!
                    </p>
                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => setShowNewPost(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Post
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Categories */}
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-navy-900 dark:text-white">Categories</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {categories.map(cat => {
                    const Icon = CATEGORY_ICONS[cat.id] || Users;
                    return (
                      <button
                        key={cat.id}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                          selectedCategory === cat.id 
                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' 
                            : 'hover:bg-navy-50 dark:hover:bg-navy-700 text-navy-600 dark:text-navy-300'
                        }`}
                        onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Community Guidelines */}
              <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-navy-900 dark:text-white">
                    <Award className="w-4 h-4 text-amber-600" />
                    Community Standards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-navy-500 dark:text-navy-400">
                    <li>• Professional communication</li>
                    <li>• Career-focused discussion</li>
                    <li>• No spam or self-promotion</li>
                    <li>• Supportive environment</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Single Post View */}
        {activeView === 'post' && selectedPost && (
          <div className="max-w-3xl mx-auto">
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
              <CardContent className="p-6">
                {loadingPost ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                  </div>
                ) : (
                  <>
                    {/* Post Header */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        {selectedPost.user_picture ? (
                          <img src={selectedPost.user_picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-white font-semibold text-xl">
                            {selectedPost.user_name?.charAt(0)?.toUpperCase() || 'M'}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-navy-900 dark:text-white">{selectedPost.user_name}</p>
                        <div className="flex items-center gap-2 text-sm text-navy-500 dark:text-navy-400">
                          <span>{formatTimeAgo(selectedPost.created_at)}</span>
                          <span>•</span>
                          <Badge className={CATEGORY_COLORS[selectedPost.category]}>
                            {getCategoryName(selectedPost.category)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Post Content */}
                    <h2 className="font-heading text-2xl font-bold text-navy-900 dark:text-white mb-4">
                      {selectedPost.title}
                    </h2>
                    <p className="text-navy-700 dark:text-navy-200 whitespace-pre-wrap leading-relaxed mb-6">
                      {selectedPost.content}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-4 pb-6 border-b border-navy-100 dark:border-navy-700">
                      <Button
                        variant="ghost"
                        className={`gap-2 ${selectedPost.user_has_liked ? 'text-red-500' : 'text-navy-500 dark:text-navy-400'}`}
                        onClick={() => handleLikePost(selectedPost.post_id)}
                      >
                        <Heart className={`w-5 h-5 ${selectedPost.user_has_liked ? 'fill-current' : ''}`} />
                        {selectedPost.likes_count || 0} likes
                      </Button>
                      <span className="text-navy-500 dark:text-navy-400">
                        <MessageSquare className="w-5 h-5 inline mr-2" />
                        {selectedPost.comments_count || 0} comments
                      </span>
                    </div>

                    {/* Comments Section */}
                    <div className="pt-6">
                      <h3 className="font-heading font-semibold text-navy-900 dark:text-white mb-4">
                        Comments ({postComments.length})
                      </h3>
                      
                      {/* New Comment */}
                      <div className="flex gap-3 mb-6">
                        <div className="w-10 h-10 bg-navy-200 dark:bg-navy-600 rounded-full flex items-center justify-center flex-shrink-0">
                          {user?.picture ? (
                            <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <span className="text-navy-600 dark:text-navy-300 font-semibold">
                              {user?.name?.charAt(0)?.toUpperCase() || 'M'}
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <Textarea
                            placeholder="Write a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            className="min-h-[80px] dark:bg-navy-700 dark:border-navy-600 mb-2"
                            data-testid="comment-input"
                          />
                          <Button 
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            data-testid="submit-comment-btn"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Comment
                          </Button>
                        </div>
                      </div>

                      {/* Comments List */}
                      <div className="space-y-4">
                        {postComments.map(comment => (
                          <div key={comment.comment_id} className="flex gap-3">
                            <div className="w-10 h-10 bg-navy-200 dark:bg-navy-600 rounded-full flex items-center justify-center flex-shrink-0">
                              {comment.user_picture ? (
                                <img src={comment.user_picture} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <span className="text-navy-600 dark:text-navy-300 font-semibold text-sm">
                                  {comment.user_name?.charAt(0)?.toUpperCase() || 'M'}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 bg-navy-50 dark:bg-navy-700/50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="font-medium text-navy-900 dark:text-white">
                                    {comment.user_name}
                                  </span>
                                  <span className="text-sm text-navy-500 dark:text-navy-400 ml-2">
                                    {formatTimeAgo(comment.created_at)}
                                  </span>
                                </div>
                              </div>
                              <p className="text-navy-700 dark:text-navy-200 mb-2">{comment.content}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`-ml-2 gap-1 ${comment.user_has_liked ? 'text-red-500' : 'text-navy-400'}`}
                                onClick={() => handleLikeComment(comment.comment_id)}
                              >
                                <Heart className={`w-3 h-3 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                {comment.likes_count || 0}
                              </Button>
                            </div>
                          </div>
                        ))}
                        
                        {postComments.length === 0 && (
                          <p className="text-center text-navy-500 dark:text-navy-400 py-8">
                            No comments yet. Be the first to comment!
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Community;
